import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "@config/env";

const STAGES = [
  { key:"shortlisted", label:"Shortlisted", color:"#2563EB", status:"1" },
  { key:"interview",   label:"Interview",   color:"#D97706", status:"2" },
  { key:"presented",   label:"Presented",   color:"#16A34A", status:"3" },
  { key:"rejected",    label:"Rejected",    color:"#DC2626", status:"4" },
];

export default function JobFillRate({ userSlug }) {
  const [jobs,    setJobs]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userSlug) return;
    const fd = new FormData();
    fd.append("user_slug", userSlug);
    fd.append("search_text", "");
    // this widget computes aggregate stats (total/fill rate/coverage) across
    // every active job, so it needs the full set, not one page of results
    fd.append("per_page", "1000");
    axios.post(`${API_BASE}get-latest-job-postings`, fd)
      .then(res => {
        setJobs(res.data?.job_list || res.data?.data || []);
        setLoading(false);
      }).catch(() => setLoading(false));
  }, [userSlug]);

  const total       = jobs.length;
  const withCandidates = jobs.filter(j => Number(j.candidates_count || 0) > 0).length;
  // NOTE: there's no "Accepted"/"Placed" candidate status in the system today -
  // candidate_job_status only has Shortlisted/Interview/Presented/Rejected. Using
  // "Presented" (status 3, i.e. submitted to the client) as the closest proxy for
  // a job being "filled" until there's a dedicated final status to key off of.
  const accepted    = jobs.filter(j => Number(j.presented_count || 0) > 0).length;
  const fillRate    = total > 0 ? Math.round((accepted / total) * 100) : 0;
  const coverRate   = total > 0 ? Math.round((withCandidates / total) * 100) : 0;

  const stages = STAGES.map(s => ({
    ...s,
    count: jobs.reduce((acc, j) => acc + Number(j[`${s.key}_count`] || 0), 0)
  }));
  const stageTotal = stages.reduce((a, b) => a + b.count, 0);

  if (loading) return (
    <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-card-border)", borderRadius:16, padding:"20px", boxShadow:"var(--fc-card-shadow)" }}>
      <div style={{ height:12, background:"var(--fc-border-light)", borderRadius:4, width:"45%", marginBottom:16 }} />
      <div style={{ height:8, background:"var(--fc-border-light)", borderRadius:99, marginBottom:14 }} />
      <div style={{ display:"flex", gap:8 }}>
        {[1,2,3,4].map(i => <div key={i} style={{ flex:1, height:44, background:"var(--fc-bg-light)", borderRadius:10 }} />)}
      </div>
    </div>
  );

  return (
    <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-card-border)", borderRadius:16, padding:"20px", boxShadow:"var(--fc-card-shadow)" }}>
      <div style={{ fontSize:13, fontWeight:700, color:"var(--fc-text-primary)", marginBottom:14, display:"flex", alignItems:"center", gap:7 }}>
        <i className="fa-solid fa-chart-bar" style={{ color:"var(--fc-primary)", fontSize:13 }} />
        Job Pipeline
      </div>

      {total === 0 ? (
        <div style={{ fontSize:12, color:"var(--fc-text-subtle)", textAlign:"center", padding:"12px 0" }}>No active jobs</div>
      ) : (
        <>
          {/* Fill rate + coverage */}
          <div style={{ display:"flex", gap:12, marginBottom:16 }}>
            {[
              { label:"Fill Rate",   value:`${fillRate}%`,  color:"var(--fc-green)",  bg:"var(--fc-green-light)",  sub:`${accepted} of ${total} filled` },
              { label:"Coverage",    value:`${coverRate}%`, color:"var(--fc-primary)", bg:"var(--fc-primary-light)", sub:`${withCandidates} with candidates` },
              { label:"Total Jobs",  value:total,           color:"var(--fc-purple)", bg:"var(--fc-purple-light)", sub:"active positions" },
            ].map((m, i) => (
              <div key={i} style={{ flex:1, background:m.bg, borderRadius:10, padding:"10px 12px" }}>
                <div style={{ fontSize:10, fontWeight:700, color:m.color, textTransform:"uppercase", letterSpacing:"0.07em", opacity:0.8, marginBottom:3 }}>{m.label}</div>
                <div style={{ fontSize:20, fontWeight:800, color:m.color, lineHeight:1, marginBottom:2 }}>{m.value}</div>
                <div style={{ fontSize:10, color:m.color, opacity:0.65 }}>{m.sub}</div>
              </div>
            ))}
          </div>

          {/* Stage pipeline bar */}
          {stageTotal > 0 && (
            <>
              <div style={{ fontSize:11, fontWeight:600, color:"var(--fc-text-subtle)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>Candidate Pipeline</div>
              <div style={{ display:"flex", borderRadius:8, overflow:"hidden", height:10, marginBottom:10 }}>
                {stages.map((s, i) => {
                  const pct = stageTotal > 0 ? (s.count / stageTotal) * 100 : 0;
                  if (pct === 0) return null;
                  return <div key={i} title={`${s.label}: ${s.count}`} style={{ width:`${pct}%`, background:s.color, transition:"width 0.5s" }} />;
                })}
              </div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:10 }}>
                {stages.filter(s => s.count > 0).map(s => (
                  <div key={s.key} style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:s.color, flexShrink:0 }} />
                    <span style={{ fontSize:11, color:"var(--fc-text-muted)" }}>{s.label}</span>
                    <span style={{ fontSize:11, fontWeight:700, color:"var(--fc-text-primary)" }}>{s.count}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
